import { useEffect, useState } from 'react'
import { useTourStore } from '../store/tourStore.js'
import { TOUR_STEPS } from '../data/tourSteps.js'
import { TOUR_DEMOS } from './tour/tourDemos.jsx'

/**
 * GuidedTour — the modal overlay that walks new users through the app.
 *
 * Architecture:
 *   - A fixed-position dim backdrop covers the whole page
 *   - A "spotlight" element positioned over the target uses a huge
 *     box-shadow to create the cutout effect — feels like a light is
 *     pointed at the element
 *   - A tooltip with title/body/progress/Next/Back/Skip renders near
 *     the target (or centered on the screen if target is null)
 *   - Auto-scrolls the target into view when a step changes
 *
 * Keyboard shortcuts:
 *   →  Next step
 *   ←  Previous step
 *   Esc Skip
 *
 * If a step's target element can't be found on the page, we center the
 * tooltip rather than skipping the step — the copy still explains the
 * feature and the user can discover it themselves.
 */
export default function GuidedTour() {
  const isOpen = useTourStore((s) => s.isOpen)
  const stepIndex = useTourStore((s) => s.stepIndex)
  const next = useTourStore((s) => s.next)
  const back = useTourStore((s) => s.back)
  const close = useTourStore((s) => s.close)
  const skip = useTourStore((s) => s.skip)

  const [targetRect, setTargetRect] = useState(null)
  const step = TOUR_STEPS[stepIndex]

  // Measure the target element each time the step changes (or on resize).
  useEffect(() => {
    if (!isOpen || !step) return
    const measure = () => {
      if (!step.target) {
        setTargetRect(null)
        return
      }
      const el = document.querySelector(step.target)
      if (!el) {
        setTargetRect(null)
        return
      }
      // Scroll the target into view gently
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      // Measure after scroll settles
      setTimeout(() => {
        const r = el.getBoundingClientRect()
        setTargetRect({
          top: r.top,
          left: r.left,
          width: r.width,
          height: r.height,
        })
      }, 250)
    }
    measure()
    const onResize = () => measure()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [isOpen, step])

  // Keyboard shortcuts
  useEffect(() => {
    if (!isOpen) return
    const onKey = (e) => {
      if (e.key === 'Escape') skip()
      else if (e.key === 'ArrowRight') {
        if (stepIndex < TOUR_STEPS.length - 1) next()
        else close()
      } else if (e.key === 'ArrowLeft') back()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isOpen, stepIndex, next, back, close, skip])

  if (!isOpen || !step) return null

  const isFirst = stepIndex === 0
  const isLast = stepIndex === TOUR_STEPS.length - 1

  return (
    <>
      {/* Dim backdrop — use a big box-shadow around the spotlight to dim
          everything outside the target rect. If no target, a flat overlay. */}
      {targetRect ? (
        <div
          onClick={skip}
          style={{
            position: 'fixed',
            top: targetRect.top - 6,
            left: targetRect.left - 6,
            width: targetRect.width + 12,
            height: targetRect.height + 12,
            borderRadius: 10,
            boxShadow: '0 0 0 9999px rgba(17, 24, 39, 0.55)',
            zIndex: 1500,
            pointerEvents: 'auto',
            transition: 'all 0.2s ease-out',
          }}
        />
      ) : (
        <div
          onClick={skip}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(17, 24, 39, 0.55)',
            zIndex: 1500,
          }}
        />
      )}

      {/* Tooltip */}
      <TourTooltip
        step={step}
        stepIndex={stepIndex}
        totalSteps={TOUR_STEPS.length}
        targetRect={targetRect}
        onNext={() => (isLast ? close() : next())}
        onBack={back}
        onSkip={skip}
        isFirst={isFirst}
        isLast={isLast}
      />
    </>
  )
}

/* ─── Tooltip ─────────────────────────────────────────────────────── */

const TOOLTIP_WIDTH = 340

function TourTooltip({
  step,
  stepIndex,
  totalSteps,
  targetRect,
  onNext,
  onBack,
  onSkip,
  isFirst,
  isLast,
}) {
  const placement = step.placement || 'bottom'
  const style = computeTooltipStyle(targetRect, placement)

  return (
    <div
      role="dialog"
      aria-label={`Tour step ${stepIndex + 1} of ${totalSteps}: ${step.title}`}
      style={{
        position: 'fixed',
        ...style,
        width: TOOLTIP_WIDTH,
        maxWidth: '92vw',
        maxHeight: 'calc(100vh - 24px)',
        overflowY: 'auto',
        background: '#fff',
        borderRadius: 12,
        boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
        padding: 12,
        zIndex: 1600,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 10,
          fontWeight: 700,
          color: 'var(--muted)',
          textTransform: 'uppercase',
          letterSpacing: 0.08,
        }}
      >
        <span>Step {stepIndex + 1} of {totalSteps}</span>
        <div
          style={{
            flex: 1,
            height: 3,
            background: '#e5e7eb',
            borderRadius: 99,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${((stepIndex + 1) / totalSteps) * 100}%`,
              height: '100%',
              background: 'var(--accent)',
              transition: 'width 0.2s',
            }}
          />
        </div>
      </div>

      <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: 'var(--text)' }}>
        {step.title}
      </h3>

      {step.media && <TourMedia media={step.media} />}

      <p
        style={{
          margin: 0,
          fontSize: 13,
          color: 'var(--text-secondary)',
          lineHeight: 1.5,
          whiteSpace: 'pre-line',
        }}
      >
        {step.body}
      </p>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginTop: 4,
        }}
      >
        <button
          onClick={onSkip}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--muted)',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            padding: 0,
            textDecoration: 'underline',
            textDecorationStyle: 'dotted',
          }}
        >
          Skip tour
        </button>
        <div style={{ flex: 1 }} />
        {!isFirst && (
          <button
            onClick={onBack}
            style={{
              padding: '6px 14px',
              border: '1px solid var(--border)',
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 600,
              background: '#fff',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
            }}
          >
            Back
          </button>
        )}
        <button
          onClick={onNext}
          style={{
            padding: '6px 16px',
            border: 'none',
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 700,
            background: 'var(--accent)',
            color: '#fff',
            cursor: 'pointer',
          }}
        >
          {isLast ? 'Finish' : 'Next →'}
        </button>
      </div>
    </div>
  )
}

/* ─── Media ──────────────────────────────────────────────────────── */

/**
 * TourMedia — renders an optional image/GIF/video/iframe above the step
 * body. Failing gracefully is important since tour media is optional
 * and often missing in development.
 */
function TourMedia({ media }) {
  if (!media) return null
  const shared = {
    width: '100%',
    maxHeight: 200,
    borderRadius: 8,
    border: '1px solid var(--border)',
    display: 'block',
    objectFit: 'cover',
    background: '#f9fafb',
  }

  if (media.kind === 'image') {
    return (
      <img
        src={media.src}
        alt={media.alt || ''}
        style={shared}
        onError={(e) => {
          // Hide broken images quietly — media is optional polish
          e.currentTarget.style.display = 'none'
        }}
      />
    )
  }
  if (media.kind === 'video') {
    return (
      <video
        src={media.src}
        poster={media.poster}
        autoPlay
        loop
        muted
        playsInline
        style={shared}
      />
    )
  }
  if (media.kind === 'iframe') {
    return (
      <iframe
        src={media.src}
        title={media.title || 'Tour media'}
        style={{ ...shared, height: 200 }}
        allow="autoplay; encrypted-media"
        allowFullScreen
      />
    )
  }
  // 'demo' — a registered animated React component
  if (media.kind === 'demo') {
    const Demo = TOUR_DEMOS[media.id]
    if (!Demo) return null
    return <Demo />
  }
  return null
}

/* ─── Placement math ──────────────────────────────────────────────── */

function computeTooltipStyle(rect, placement) {
  const margin = 12
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800
  // Rough estimate — most steps hover around 260–320px tall. We use this
  // for top-clamp math so tooltips don't collide with the viewport edge.
  // Actual content may be taller; the tooltip has overflow:auto to cope.
  const approxH = 280

  // No target → center on screen
  if (!rect || placement === 'center') {
    return {
      top: Math.max(margin, vh / 2 - approxH / 2),
      left: Math.max(margin, vw / 2 - TOOLTIP_WIDTH / 2),
    }
  }

  let top, left

  if (placement === 'top') {
    top = Math.max(margin, rect.top - approxH - margin)
    left = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2
  } else if (placement === 'left') {
    top = rect.top + rect.height / 2 - approxH / 2
    left = rect.left - TOOLTIP_WIDTH - margin
  } else if (placement === 'right') {
    top = rect.top + rect.height / 2 - approxH / 2
    left = rect.left + rect.width + margin
  } else {
    // 'bottom' default
    top = rect.top + rect.height + margin
    left = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2
  }

  // Clamp within viewport
  top = Math.max(margin, Math.min(top, vh - approxH - margin))
  left = Math.max(margin, Math.min(left, vw - TOOLTIP_WIDTH - margin))

  return { top, left }
}
